import React from "react";
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from "react-joyride";
import { Button, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { Colors } from "../../../theme";

const controlBarStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 20001,
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 320,
  padding: "6px 12px",
  background: Colors.PAPER,
  borderRadius: 8,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.25)",
};

type TourStep = Step & { route?: string };

const routes = {
  questionnaires: "/health-forms/questionnaires",
  questionGroups: "/health-forms/question-groups",
  questions: "/health-forms/questions",
  review: "/health-forms/review",
  published: "/health-forms/published",
  assistant: "/health-forms/assistant",
};

const buildSteps = (questionnaireId: number | null): TourStep[] => {
  const questionnaireRoute = questionnaireId ? `${routes.questionnaires}/${questionnaireId}` : routes.questionnaires;

  const editorSteps: TourStep[] = questionnaireId
    ? [
        {
          target: '[data-tour="hf-new"]',
          route: questionnaireRoute,
          title: "Build questionnaires",
          content:
            "The left panel lists every questionnaire. Create one with “New Questionnaire”, or search and select an existing one to open it in the editor.",
        },
        {
          target: '[data-tour="hf-editor-name"]',
          route: questionnaireRoute,
          title: "Name & identity",
          content: "Each questionnaire has an internal name used to reference it. It is not shown to patients.",
        },
        {
          target: '[data-tour="hf-page-controls"]',
          route: questionnaireRoute,
          title: "Pages",
          content:
            "A questionnaire is split into pages. Switch pages with the numbered buttons, drag a number to reorder pages, and add or delete pages here.",
        },
        {
          target: '[data-tour="hf-add-parts"]',
          route: questionnaireRoute,
          title: "Questions, groups & logic",
          content:
            "Add questions or question groups to a page, then drag to reorder them. Every part can be shown conditionally (“Visible When”, using FEEL expressions), and questions can also be marked required (“Required When”).",
        },
        {
          target: '[data-tour="hf-editor-toolbar"]',
          route: questionnaireRoute,
          title: "Save, duplicate & review",
          content:
            "Save your draft, duplicate or delete the questionnaire, or revert unsaved edits. Once there are unpublished changes, a “Review” button appears here to start publishing.",
        },
        {
          target: '[data-tour="hf-preview"]',
          route: questionnaireRoute,
          title: "Live patient preview",
          content:
            "The right panel previews the questionnaire with the same logic a patient gets — simulate a patient context (ailment, joint, market, locale, premium, therapist messaging, medications, subscriptions) and toggle Preview vs Document, and conditional questions reveal live against your simulated answers. Its exact look and feel may still differ slightly from the patient app.",
        },
      ]
    : [
        {
          target: '[data-tour="hf-new"]',
          route: questionnaireRoute,
          title: "Build questionnaires",
          content:
            "The left panel lists your questionnaires. Create one with “New Questionnaire” — the editor then opens, where you set the name, split it into pages, add questions and question groups, apply conditional logic, and see a live patient preview.",
        },
      ];

  return [
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      route: questionnaireRoute,
      title: "Welcome to Health Forms",
      content:
        "This is where clinical questionnaires are built, previewed, versioned and published. This quick tour walks through each area. Use Next / Back, or Skip to leave — you can relaunch it anytime from the “Tutorial” button.",
    },
    {
      target: '[data-tour="hf-tabs"]',
      route: questionnaireRoute,
      title: "The six areas",
      content:
        "Everything lives under these tabs. Most screens use three panels: a list on the left, an editor in the middle, and a live preview on the right.",
    },
    {
      target: '[data-tour="hf-tab-questions"]',
      route: routes.questions,
      title: "Questions — the building blocks",
      content:
        "Questions are the atomic unit. Each has localized text, a semantic key, and a response type (single / multi select, checkbox, integer, number range, VAS, date, free text, and more). “Used in” lists every questionnaire and group that references a question.",
    },
    {
      target: '[data-tour="hf-tab-questionGroups"]',
      route: routes.questionGroups,
      title: "Question Groups — reusable bundles",
      content:
        "Group related questions so they can be reused across many questionnaires — each question in a group keeps its own visibility and requirement logic.",
    },
    {
      target: '[data-tour="hf-tab-questionnaires"]',
      route: questionnaireRoute,
      title: "Questionnaires",
      content: "Assemble questions and groups into the actual patient-facing form. Let’s look inside.",
    },
    ...editorSteps,
    {
      target: '[data-tour="hf-tab-review"]',
      route: routes.review,
      title: "Review & publish",
      content:
        "Questionnaires with changes not yet published appear here. Review shows a locale-by-locale diff of what is currently published versus your draft, then lets you Publish. Publishing creates an immutable version and deploys it to EU Stage and US Stage automatically.",
    },
    {
      target: '[data-tour="hf-tab-published"]',
      route: routes.published,
      title: "Published versions",
      content:
        "Published questionnaires are versioned and read-only. Search them, expand a name to see its full version history, or compare any two versions. Each version also shows its rollout status — stage environments update automatically on publish; a Deploy button per environment group lets you deploy that exact version to stage or production.",
    },
    {
      target: "body",
      placement: "center",
      title: "States & lifecycle",
      content: (
        <div style={{ textAlign: "left" }}>
          <p style={{ marginTop: 0 }}>How a form moves through its life:</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              Questions and question groups are building blocks — just saved or unsaved (a “*” marks unsaved edits).
            </li>
            <li>
              A questionnaire goes: unsaved edits → saved draft → unpublished changes (shown in Review) → published.
            </li>
            <li>
              Publishing makes it live here and deploys it to the stage environments automatically; production only
              changes when you deploy that specific version to production, from the Published tab.
            </li>
            <li>
              Published versions are immutable — one is deployed per environment group, older ones become not deployed.
            </li>
          </ul>
        </div>
      ),
    },
    {
      target: '[data-tour="hf-tab-assistant"]',
      route: routes.assistant,
      title: "AI Assistant (coming soon)",
      content:
        "An AI assistant to build and edit forms from a chat prompt. This is an early preview — it is not connected to a live backend yet, so replies are placeholders for now.",
    },
    {
      target: "body",
      placement: "center",
      title: "That’s the tour!",
      content:
        "You have seen every part of Health Forms. Relaunch this walkthrough anytime from the “Tutorial” button next to the tabs.",
    },
  ];
};

const waitForTarget = (selector: string, timeout = 5000) =>
  new Promise<HTMLElement | null>(resolve => {
    const start = performance.now();
    const tick = () => {
      const el = document.querySelector<HTMLElement>(selector);
      if (el || performance.now() - start > timeout) {
        resolve(el);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });

const nextFrame = () =>
  new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

const HealthFormsTour: React.VFC<{
  run: boolean;
  onFinish: () => void;
  questionnaireId: number | null;
}> = ({ run, onFinish, questionnaireId }) => {
  const history = useHistory();
  const [stepIndex, setStepIndex] = React.useState(0);
  const steps = React.useMemo(() => buildSteps(questionnaireId), [questionnaireId]);
  const prevRun = React.useRef(false);

  React.useEffect(() => {
    if (run && !prevRun.current) {
      setStepIndex(0);
      const route = steps[0]?.route;
      if (route && route !== history.location.pathname) history.push(route);
    }
    prevRun.current = run;
  }, [run, steps, history]);

  const goToStep = React.useCallback(
    async (nextIndex: number) => {
      if (nextIndex < 0 || nextIndex >= steps.length) {
        onFinish();
        setStepIndex(0);
        return;
      }
      const step = steps[nextIndex];
      if (step.route && step.route !== history.location.pathname) history.push(step.route);
      if (typeof step.target === "string" && step.target !== "body") {
        const el = await waitForTarget(step.target);
        if (el) {
          el.scrollIntoView({ block: "center", inline: "nearest" });
          await nextFrame();
        }
      }
      setStepIndex(nextIndex);
    },
    [steps, history, onFinish]
  );

  const handleCallback = React.useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        onFinish();
        setStepIndex(0);
        return;
      }
      if (type === EVENTS.STEP_AFTER) {
        void goToStep(index + (action === ACTIONS.PREV ? -1 : 1));
      }
    },
    [goToStep, onFinish]
  );

  return (
    <>
      <Joyride
        run={run}
        stepIndex={stepIndex}
        steps={steps}
        callback={handleCallback}
        continuous
        disableScrolling
        disableOverlayClose
        styles={{
          options: { primaryColor: Colors.JA_BLUE, zIndex: 20000 },
          tooltipFooter: { display: "none" },
          buttonClose: { display: "none" },
        }}
      />
      {run && (
        <div style={controlBarStyle}>
          <Button
            size="small"
            onClick={() => {
              onFinish();
              setStepIndex(0);
            }}
          >
            Skip
          </Button>
          <span style={{ flex: 1 }} />
          <Typography variant="caption" color="textSecondary" style={{ whiteSpace: "nowrap" }}>
            {stepIndex + 1} / {steps.length}
          </Typography>
          <Button size="small" disabled={stepIndex === 0} onClick={() => void goToStep(stepIndex - 1)}>
            Back
          </Button>
          <Button size="small" variant="contained" color="primary" onClick={() => void goToStep(stepIndex + 1)}>
            {stepIndex === steps.length - 1 ? "Done" : "Next"}
          </Button>
        </div>
      )}
    </>
  );
};

export default HealthFormsTour;
